import { Dispatch, ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { Badge, Button, Heading, Icon, Popover, TextField } from '@alto-avios/alto-ui'
import { PopoverProps } from '@alto-avios/alto-ui/dist/components/Popover/Popover'
import { deviceSizes } from '@reward-platform/lift/theme/deviceSizes'
import { useMediaQuery } from 'usehooks-ts'
import { isClient } from '~/utils/envChecks'
import { useTranslations } from 'next-intl'
import { addRoom, removeRoom, setChildAge, setTravelers } from './roomReducer'
import * as types from './TravelerRoomPicker.constants'
import { MAX_TRAVELERS_PER_BOOKING } from './TravelerRoomPicker.constants'
import { Action, TravelerRoomPickerProps } from './TravelerRoomPicker.types'
import TravelerRoom from './TravelerRoom/TravelerRoom'
import styles from './TravelerRoomPicker.module.scss'

export const TravelerRoomPicker = ({
  rooms,
  onChange,
  getChildAgeError,
}: TravelerRoomPickerProps): ReactElement<React.PropsWithChildren> => {
  const isDesktop = useMediaQuery(`(min-width: ${deviceSizes.medium})`)
  const roomsRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLInputElement>(null)
  const popoverContentRef = useRef<HTMLDialogElement>(null)
  const shouldOpenOnNextFocusRef = useRef(false)

  const totalAdults = rooms.reduce((sum, { adults }: { adults: number }) => sum + adults, 0)
  const totalChildren = rooms.reduce((sum, { children }: { children: number }) => sum + children, 0)
  const childAgeError = rooms
    .flatMap((room, roomIndex) =>
      Array.from({ length: room.children }, (_, childIndex) =>
        getChildAgeError?.(roomIndex, childIndex)
      )
    )
    .find(Boolean)

  const t = useTranslations()
  const tLabels = useTranslations('common.labels')

  const hasChildren =
    totalChildren > 0
      ? `${t('components.traveller_picker.labels.child_count', {
          count: totalChildren,
        })}, `
      : ''

  const placeholder = `${t('components.traveller_picker.labels.adult_count', {
    count: totalAdults,
  })}, ${hasChildren}${tLabels('room_count', {
    count: rooms.length,
  })}`

  // HOTEL: Max guests per room - 7, Max guests per booking - 14
  const ableToAddRoom = totalAdults + totalChildren < MAX_TRAVELERS_PER_BOOKING && rooms.length < 4

  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const [isOpen, setIsOpen] = useState(false)
  const toggle = (state?: boolean) => {
    setIsOpen((prev) => {
      return state ?? prev
    })
  }
  const onOpen = () => {
    toggle(true)
  }
  // Close popover and return focus back to the trigger to meet WCAG focus-restore
  const onClose = () => {
    toggle(false)
    setTimeout(() => triggerRef.current?.focus(), 0)
  }

  // When the popover opens, move focus to the first interactive control inside it
  const focusPopoverContent = useCallback(() => {
    setTimeout(() => {
      const popover = popoverContentRef.current
      if (!popover) {
        return
      }
      const firstFocusable = popover.querySelector<HTMLElement>(
        'button:not([tabindex="-1"]), [href]:not([tabindex="-1"]), input:not([tabindex="-1"]), select:not([tabindex="-1"]), textarea:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
      )
      ;(firstFocusable ?? popover).focus()
    }, 0)
  }, [])

  const focusLastRoom = useCallback(() => {
    setTimeout(() => {
      const lastRoom = roomsRef.current?.lastChild as HTMLElement
      lastRoom?.focus()
    }, 100)
  }, [])

  const dispatchRoomsChange: Dispatch<Action> = useCallback(
    (action: Action) => {
      switch (action.type) {
        case 'ADD_ROOM':
          onChange(addRoom(rooms, action))
          break
        case 'REMOVE_ROOM':
          onChange(removeRoom(rooms, action))
          break
        case 'SET_TRAVELERS':
          onChange(setTravelers(rooms, action))
          break
        case 'SET_CHILD_AGE':
          onChange(setChildAge(rooms, action))
          break
        default:
          break
      }
    },
    [onChange, rooms]
  )

  const Picker = (
    <div className={styles.picker}>
      <Badge size="sm" styleVariant="brand" className={styles.travellerLimitBadge}>
        {t('components.traveller_picker.labels.maximum_number_of_travellers', {
          count: MAX_TRAVELERS_PER_BOOKING,
        })}
      </Badge>
      <div ref={roomsRef} role="list">
        {rooms?.map((room, roomIndex) => (
          <TravelerRoom
            data-testid="traveller-room-section"
            key={`${room.roomNumber}-${room.adults}-${room.children}-${room.childrenAges}`}
            room={room}
            dispatch={dispatchRoomsChange}
            totalTravelers={totalAdults + totalChildren}
            childAgeErrors={room.childrenAges?.map((_, childIndex) =>
              getChildAgeError?.(roomIndex, childIndex)
            )}
          />
        ))}
      </div>
      {ableToAddRoom && (
        <button
          type="button"
          className={styles.addRoomButton}
          onClick={() => {
            dispatchRoomsChange({
              type: types.ADD_ROOM,
              payload: { roomNumber: rooms.length + 1 },
            })
            focusLastRoom()
          }}
        >
          {t('components.traveller_picker.actions.add_another_room')}
        </button>
      )}
    </div>
  )

  const infoInput = (
    <div
      onPointerDownCapture={() => {
        shouldOpenOnNextFocusRef.current = true
      }}
    >
      <TextField
        ref={triggerRef}
        onFocus={() => {
          if (!shouldOpenOnNextFocusRef.current) {
            return
          }
          shouldOpenOnNextFocusRef.current = false
          onOpen()
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault()
            onOpen()
          }
        }}
        label={t('common.labels.travellers')}
        value={placeholder}
        isRequired
        isInvalid={!!childAgeError}
        errorMessage={childAgeError}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        id="dropdown-traveller-input"
        data-testid="dropdown-traveller-input"
      >
        {isHydrated && (
          <TextField.StartIcon>
            <Icon iconName="user" iconPrefix="fas" isDecorative />
          </TextField.StartIcon>
        )}
        {isHydrated && (
          <TextField.EndIcon>
            <Icon iconName="angles-up-down" isDecorative />
          </TextField.EndIcon>
        )}
      </TextField>
    </div>
  )

  const getPopoverPosition = () => {
    const OFFSET = 50
    const MAX_HEIGHT = 450
    const THRESHOLD = 150

    const { top = 0, bottom = 0 } = triggerRef.current?.getBoundingClientRect() ?? {}

    const available = {
      below: isClient() ? window.innerHeight - bottom - OFFSET : 0,
      above: top - OFFSET,
    }

    const maxSpace = {
      below: Math.min(MAX_HEIGHT, available.below),
      above: Math.min(MAX_HEIGHT, available.above),
    }

    const opensUp = maxSpace.below < THRESHOLD
    const maxHeightDesktop = opensUp ? maxSpace.above : maxSpace.below
    const maxHeight = isDesktop ? `${maxHeightDesktop}px` : '100vh'

    return {
      arrowDirection: (opensUp ? 'top' : 'bottom') as PopoverProps['arrowDirection'],
      maxHeight: isDesktop ? maxHeight : '100vh',
    }
  }

  const { arrowDirection, maxHeight } = getPopoverPosition()

  useEffect(() => {
    if (isOpen) {
      focusPopoverContent()
    }
  }, [focusPopoverContent, isOpen])

  return (
    <>
      {infoInput}
      {isOpen ? (
        <Popover
          hasArrow={false}
          arrowDirection={arrowDirection}
          state={{
            close: onClose,
            isOpen,
            open: onOpen,
            toggle,
            setOpen: onOpen,
          }}
          triggerRef={triggerRef as PopoverProps['triggerRef']}
        >
          <dialog
            open
            ref={popoverContentRef}
            className={styles.popOverContent}
            aria-label="traveler room picker"
            style={{ maxHeight }}
          >
            {!isDesktop ? (
              <>
                <Button onPress={onClose} styleVariant="white">
                  <Icon iconName="xmark" iconSize="1.5x" />
                </Button>
                <Heading as="h4" className={styles.dialogHeader}>
                  {t('common.labels.travellers')}
                </Heading>
              </>
            ) : null}
            {Picker}
            <Button size="sm" onPress={onClose} fullWidth>
              {t('common.labels.done')}
            </Button>
          </dialog>
        </Popover>
      ) : null}
    </>
  )
}
